import { isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  applyNotificationDerivedFields,
  conditionFingerprint,
  conditionValues,
  findConflictingNotificationRule,
  findDuplicateNotificationRule,
  findOverlappingNotificationRules,
  formatConditionLine,
  formatTimingReferenceDetail,
  getAvailableConditionFields,
  getConditionOperators,
  getConditionValueOptions,
  getDestinationOptions,
  getDestinationRequirement,
  getEventDefinition,
  getEventDisplay,
  getMethodLabel,
  getMethodOptions,
  getRecipientLabel,
  getRecipientOptions,
  getReferencedMaximumSends,
  getSendToLabel,
  getSendToOptions,
  getTimingLabel,
  getTimingOptions,
  getTimingReference,
  getTypeLabel,
  getTypeOptions,
  getWordingFields,
  getWordingLimit,
  isTimingReferenceSelectable,
  namesMatch,
  needsRepeatInterval,
  nextNotificationRuleId,
  normalizeNotificationForm,
  operatorIsMulti,
} from './notificationsConfig';
import { emitConfigurationHistoryBatch } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  team: 'Platform Operations',
  accountId: 'ADM-0002',
};

const PLACEHOLDER_PATTERN = /\{+[^{}]+\}+/g;

const TRACKED_FIELDS = [
  'ruleName',
  'event',
  'recipient',
  'sendTo',
  'method',
  'type',
  'timing',
  'initialDelay',
  'maximumSends',
  'repeatInterval',
  'dashboardTitle',
  'dashboardMessage',
  'emailSubject',
  'emailBody',
  'browserPushTitle',
  'shortMessage',
  'destinationLink',
  'conditions',
];

export function notificationRuleActor() {
  return { ...ACTOR };
}

export function canAccessNotificationsConfiguration(auth) {
  return isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);
}

export function canViewNotificationsConfiguration(auth) {
  return canAccessNotificationsConfiguration(auth);
}

export function canAddNotificationRule(auth) {
  return canAccessNotificationsConfiguration(auth);
}

export function canEditNotificationRule(auth) {
  return canAccessNotificationsConfiguration(auth);
}

export function canSaveNotificationRule(auth) {
  return canAccessNotificationsConfiguration(auth);
}

function addError(result, key, message) {
  if (!result.summary.includes(message)) result.summary.push(message);
  if (key && !result.errors[key]) result.errors[key] = message;
}

function isBlank(value) {
  return value == null || !String(value).trim();
}

function conditionsCannotAllBeTrue(conditions = []) {
  const byField = {};
  conditions.forEach((condition) => {
    if (!condition.field || !condition.operator) return;
    const values = conditionValues(condition);
    if (!values.length) return;
    if (!byField[condition.field]) byField[condition.field] = [];
    byField[condition.field].push(values);
  });
  return Object.values(byField).some((entries) => {
    const exclusive = entries.filter((values) => values.length === 1).map((values) => values[0]);
    return new Set(exclusive).size > 1;
  });
}

function extractPlaceholders(text) {
  return String(text || '').match(PLACEHOLDER_PATTERN) || [];
}

export function collectNotificationSaveErrors(formData, allRules, excludeId, options = {}) {
  const { originalLastUpdated, limitRules, canSave, checksAvailable } = options;
  const form = applyNotificationDerivedFields(normalizeNotificationForm(formData));
  const result = { errors: {}, summary: [] };
  const others = (allRules || []).filter((rule) => rule.id !== excludeId).map(normalizeNotificationForm);
  const event = getEventDefinition(form.event);

  if (canSave === false) {
    addError(result, 'permission', 'You do not have permission to save this Rule.');
    return result;
  }
  if (checksAvailable === false) {
    addError(result, 'checksUnavailable', 'The required comparison could not be completed.');
    return result;
  }

  if (isBlank(form.ruleName)) addError(result, 'ruleName', 'Enter a Rule Name.');
  else if (others.some((rule) => namesMatch(rule.ruleName, form.ruleName))) {
    addError(result, 'ruleName', 'This Rule Name is already used.');
  }

  if (!form.event) addError(result, 'event', 'Select an Event.');
  else if (!event) addError(result, 'event', 'This reference is unavailable or incompatible.');
  else if (event.available === false) addError(result, 'event', 'This reference is unavailable or incompatible.');
  else if (event.complete === false || !event.id || !event.label) {
    addError(result, 'event', 'This Event definition is incomplete.');
  }

  const recipientOptions = getRecipientOptions(form.event);
  if (!form.recipient) addError(result, 'recipient', 'Select a compatible recipient.');
  else if (!recipientOptions.some((option) => option.value === form.recipient)) {
    addError(result, 'recipient', 'Select a compatible recipient.');
  }

  const sendToOptions = getSendToOptions(form.event, form.recipient);
  if (!form.sendTo) addError(result, 'sendTo', 'Select a compatible recipient.');
  else if (!sendToOptions.some((option) => option.value === form.sendTo)) {
    addError(result, 'sendTo', 'Select a compatible recipient.');
  }

  const methodOptions = getMethodOptions(form.event, form.recipient);
  if (!form.method) addError(result, 'method', 'Select a compatible Method.');
  else if (!methodOptions.some((option) => option.value === form.method)) {
    addError(result, 'method', 'Select a compatible Method.');
  }

  const typeOptions = getTypeOptions(form.event);
  if (!form.type || !typeOptions.some((option) => option.value === form.type)) {
    addError(result, 'type', 'Select Required or Optional.');
  }

  const timingOptions = getTimingOptions(form.event);
  if (!form.timing) addError(result, 'timing', 'Select a compatible Timing.');
  else if (!timingOptions.some((option) => option.value === form.timing)) {
    addError(result, 'timing', 'Select a compatible Timing.');
  }

  if (form.timing === 'threshold_based') {
    if (!event?.timingContext) addError(result, 'timing', 'Select a compatible Timing.');
    if (!form.initialDelay) addError(result, 'initialDelay', 'Select an Initial Delay.');
    else {
      const delay = getTimingReference(form.initialDelay, limitRules);
      if (!isTimingReferenceSelectable(delay, 'initial_send_delay')) {
        addError(result, 'initialDelay', 'This reference is unavailable or incompatible.');
      }
    }
    if (!form.maximumSends) addError(result, 'maximumSends', 'Select a Maximum Sends Rule.');
    else {
      const maximum = getTimingReference(form.maximumSends, limitRules);
      if (!isTimingReferenceSelectable(maximum, 'maximum_sends')) {
        addError(result, 'maximumSends', 'This reference is unavailable or incompatible.');
      } else {
        const sends = getReferencedMaximumSends(form.maximumSends, limitRules);
        if (form.type === 'required' && sends === 0) {
          addError(result, 'maximumSends', 'A Required notification must permit at least one send.');
        }
        if (needsRepeatInterval(form.maximumSends, limitRules)) {
          if (!form.repeatInterval) addError(result, 'repeatInterval', 'Select a Repeat Interval.');
          else {
            const interval = getTimingReference(form.repeatInterval, limitRules);
            if (!isTimingReferenceSelectable(interval, 'repeat_send_interval')) {
              addError(result, 'repeatInterval', 'This reference is unavailable or incompatible.');
            }
          }
        }
      }
    }
  }

  const wordingFields = getWordingFields(form.method);
  wordingFields.forEach((field) => {
    const text = String(form[field.key] || '');
    if (!text.trim()) {
      addError(result, field.key, 'Complete the required wording.');
      return;
    }
    const limit = getWordingLimit(form.event, field.key);
    if (limit == null) {
      addError(result, field.key, 'The required wording check could not be completed.');
      return;
    }
    if (text.length > limit) addError(result, field.key, 'This wording exceeds the allowed limit.');
    const allowed = new Set(event?.placeholders || []);
    extractPlaceholders(text).forEach((token) => {
      const name = token.replace(/^\{+/, '').replace(/\}+$/, '');
      if (!allowed.has(token) && !allowed.has(name)) {
        addError(result, field.key, 'This placeholder is unavailable for the selected Event.');
      }
    });
  });

  const destinationRequirement = getDestinationRequirement(form.event);
  if (form.event && !destinationRequirement) {
    addError(result, 'destinationLink', 'The destination requirement is unavailable.');
  } else if (destinationRequirement === 'required') {
    const destinations = getDestinationOptions(form.event);
    if (!destinations.length) addError(result, 'destinationLink', 'No compatible destinations available.');
    else if (!form.destinationLink) addError(result, 'destinationLink', 'Select a Destination Link.');
    else if (!destinations.some((option) => option.value === form.destinationLink)) {
      addError(result, 'destinationLink', 'This reference is unavailable or incompatible.');
    }
  } else if (destinationRequirement === 'optional' && form.destinationLink) {
    const destinations = getDestinationOptions(form.event);
    if (!destinations.some((option) => option.value === form.destinationLink)) {
      addError(result, 'destinationLink', 'This reference is unavailable or incompatible.');
    }
  }

  const availableFields = getAvailableConditionFields(form.event).map((option) => option.value);
  const seenFingerprints = [];
  (form.conditions || []).forEach((condition, index) => {
    const key = `condition_${index}`;
    const values = conditionValues(condition);
    if (!condition.field || !condition.operator || !values.length) {
      addError(result, key, 'Complete or remove this Condition.');
      return;
    }
    if (!availableFields.includes(condition.field)) {
      addError(result, key, 'This reference is unavailable or incompatible.');
    }
    if (!getConditionOperators(condition.field).some((option) => option.value === condition.operator)) {
      addError(result, key, 'This reference is unavailable or incompatible.');
    }
    if (!operatorIsMulti(condition.operator) && values.length !== 1) {
      addError(result, key, 'Complete or remove this Condition.');
    }
    const valueOptions = getConditionValueOptions(condition.field).map((option) => option.value);
    if (!valueOptions.length) addError(result, key, 'No compatible values available.');
    else if (values.some((value) => !valueOptions.includes(value))) {
      addError(result, key, 'This reference is unavailable or incompatible.');
    }
    const fingerprint = conditionFingerprint(condition);
    if (seenFingerprints.includes(fingerprint)) addError(result, key, 'This Condition is already included.');
    seenFingerprints.push(fingerprint);
  });

  if (conditionsCannotAllBeTrue(form.conditions)) {
    addError(result, 'conditions', 'These Conditions cannot all be true together.');
  }

  const duplicate = findDuplicateNotificationRule(others, form);
  if (duplicate) addError(result, 'duplicate', `This Rule duplicates ${duplicate.id}.`);

  const conflict = findConflictingNotificationRule(others, form);
  if (conflict) addError(result, 'conflict', `This Rule conflicts with ${conflict.id}.`);

  const overlaps = findOverlappingNotificationRules(others, form)
    .filter((rule) => rule.id !== duplicate?.id && rule.id !== conflict?.id);
  if (overlaps.length) {
    addError(result, 'overlap', `This Rule overlaps ${overlaps.map((rule) => rule.id).join(' and ')}.`);
  }

  if (excludeId && originalLastUpdated) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    if (current && (current.lastUpdated || current.modifiedOn) && (current.lastUpdated || current.modifiedOn) !== originalLastUpdated) {
      addError(result, 'concurrent', 'This Rule has changed. Review the latest saved Rule.');
    }
  }

  return result;
}

export function ruleSnapshotFromForm(formData, limitRules) {
  const form = applyNotificationDerivedFields(normalizeNotificationForm(formData));
  const wording = {};
  getWordingFields(form.method).forEach((field) => {
    wording[field.key] = String(form[field.key] || '').trim();
  });
  const threshold = form.timing === 'threshold_based';
  const keepRepeat = threshold && needsRepeatInterval(form.maximumSends, limitRules);
  return {
    ruleName: String(form.ruleName || '').trim(),
    event: form.event,
    recipient: form.recipient,
    sendTo: form.sendTo,
    method: form.method,
    type: form.type,
    timing: form.timing,
    initialDelay: threshold ? form.initialDelay : '',
    maximumSends: threshold ? form.maximumSends : '',
    repeatInterval: keepRepeat ? form.repeatInterval : '',
    dashboardTitle: '',
    dashboardMessage: '',
    emailSubject: '',
    emailBody: '',
    browserPushTitle: '',
    shortMessage: '',
    ...wording,
    destinationLink: getDestinationRequirement(form.event) === 'not_available' ? '' : form.destinationLink,
    conditions: (form.conditions || []).map((condition) => ({
      id: condition.id,
      field: condition.field,
      operator: condition.operator,
      values: conditionValues(condition),
    })),
  };
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeNotificationForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizeNotificationForm({ ...rule, pendingDraft: null });
}

function fieldLabel(key) {
  const labels = {
    ruleName: 'Rule Name',
    event: 'Event',
    recipient: 'Recipient Type',
    sendTo: 'Recipient',
    method: 'Method',
    type: 'Required / Optional',
    timing: 'Timing',
    initialDelay: 'Initial Delay',
    maximumSends: 'Maximum Sends',
    repeatInterval: 'Repeat Interval',
    dashboardTitle: 'Dashboard Title',
    dashboardMessage: 'Dashboard Message',
    emailSubject: 'Email Subject',
    emailBody: 'Email Body',
    browserPushTitle: 'Browser Push Title',
    shortMessage: 'Short Message',
    destinationLink: 'Destination Link',
    conditions: 'Conditions',
  };
  return labels[key] || key;
}

function displayFieldValue(key, value, context = {}) {
  if (key === 'conditions') {
    if (!value?.length) return 'No Conditions';
    return value.map(formatConditionLine).join(' AND ');
  }
  if (value == null || value === '') return '—';
  if (key === 'event') return getEventDisplay(value);
  if (key === 'recipient') return getRecipientLabel(value);
  if (key === 'sendTo') return getSendToLabel(value);
  if (key === 'method') return getMethodLabel(value);
  if (key === 'type') return getTypeLabel(value);
  if (key === 'timing') return getTimingLabel(value);
  if (key === 'destinationLink') return String(value);
  return String(value);
}

export function diffRuleSnapshots(prev, next) {
  const changes = [];
  TRACKED_FIELDS.forEach((key) => {
    const oldDisplay = displayFieldValue(key, prev?.[key], prev || {});
    const newDisplay = displayFieldValue(key, next?.[key], next || {});
    if (oldDisplay !== newDisplay) {
      changes.push({ field: fieldLabel(key), previousValue: oldDisplay, newValue: newDisplay });
    }
  });
  return changes;
}

function snapshotsEqual(a, b) {
  return diffRuleSnapshots(a, b).length === 0;
}

export function appendConfigurationHistory(rule, action, changes, status, version) {
  const ts = new Date().toISOString();
  const actor = notificationRuleActor();
  const base = Array.isArray(rule.configurationHistory) ? rule.configurationHistory : [];
  const changeRows = changes.length ? changes : [{ field: '—', previousValue: '—', newValue: '—' }];
  const entries = changeRows.map((c) => ({
    ruleId: rule.id,
    version,
    action,
    field: c.field,
    previousValue: c.previousValue,
    newValue: c.newValue,
    status,
    actor: actor.name,
    role: actor.role,
    accountId: actor.accountId,
    timestamp: ts,
    changeReason: 'Not Required',
  }));
  try {
    emitConfigurationHistoryBatch({
      rows: entries,
      module: 'Configuration — Notifications',
      sourceModuleRef: 'configuration_notifications',
      changedItem: rule.ruleName || rule.id,
      itemId: rule.id,
      actor: { name: actor.name, role: actor.role },
      stamp: ts,
      changeType: action === 'Add' || action === 'Created' ? 'Add' : 'Edit',
      changeReason: 'Not Required',
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  return [...base, ...entries];
}

export function appendTestHistory(rule, summary, passed) {
  const actor = notificationRuleActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline, limitRules) {
  const snapshot = ruleSnapshotFromForm(payload, limitRules);
  const isNew = !publishedBaseline?.id;
  const actor = notificationRuleActor();
  const nowIso = payload.lastUpdated || new Date().toISOString();

  if (!isNew) {
    const prevSnap = ruleSnapshotFromForm(publishedBaseline, limitRules);
    if (snapshotsEqual(prevSnap, snapshot)) {
      return {
        rules: allRules,
        record: getPublishedRule(publishedBaseline),
        unchanged: true,
        isNew: false,
      };
    }
  }

  const id = isNew ? payload.id || nextNotificationRuleId(allRules) : publishedBaseline.id;
  const changes = isNew
    ? diffRuleSnapshots({}, snapshot)
    : diffRuleSnapshots(ruleSnapshotFromForm(publishedBaseline, limitRules), snapshot);

  const record = {
    ...(isNew ? {} : getPublishedRule(publishedBaseline)),
    ...snapshot,
    id,
    version: isNew ? 1 : publishedBaseline.version || 1,
    pendingDraft: null,
    configurationHistory: appendConfigurationHistory(
      {
        id,
        ruleName: snapshot.ruleName,
        configurationHistory: publishedBaseline?.configurationHistory || [],
      },
      isNew ? 'Add' : 'Edit',
      changes,
      'saved',
      isNew ? 1 : publishedBaseline.version || 1,
    ),
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || publishedBaseline.createdOn || nowIso,
    addedBy: isNew ? actor.name : publishedBaseline.addedBy || publishedBaseline.createdBy || actor.name,
    lastUpdated: nowIso,
    lastUpdatedBy: actor.name,
  };

  const rules = isNew
    ? [...allRules.filter((r) => r.id !== record.id), record]
    : allRules.map((r) => (r.id === record.id ? record : r));

  return { rules, record, unchanged: false, isNew };
}

export function saveNotificationRule(allRules, payload, publishedBaseline, options = {}) {
  const excludeId = publishedBaseline?.id || payload?.id || undefined;
  const validation = collectNotificationSaveErrors(payload, allRules, excludeId, {
    originalLastUpdated:
      options.originalLastUpdated
      ?? publishedBaseline?.lastUpdated
      ?? publishedBaseline?.modifiedOn,
    limitRules: options.limitRules,
    canSave: options.canSave !== false,
    checksAvailable: options.checksAvailable !== false,
  });

  if (validation.summary.length) {
    return {
      success: false,
      errors: validation.errors,
      summary: validation.summary,
      rules: allRules,
      unchanged: false,
    };
  }

  try {
    const merged = mergeRulesAfterSave(allRules, payload, publishedBaseline || {}, options.limitRules);
    return {
      success: true,
      errors: {},
      summary: [],
      rules: merged.rules,
      record: merged.record,
      unchanged: Boolean(merged.unchanged),
      isNew: Boolean(merged.isNew),
    };
  } catch {
    return {
      success: false,
      errors: { form: 'The Rule could not be saved. Your changes have been kept for review.' },
      summary: ['The Rule could not be saved. Your changes have been kept for review.'],
      rules: allRules,
      unchanged: false,
    };
  }
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline, limitRules) {
  const result = saveNotificationRule(
    allRules,
    payload,
    publishedBaseline || {},
    { canSave: true, checksAvailable: true, limitRules },
  );
  return result.rules;
}

export function collectActivationErrors(formData, allRules, excludeId) {
  return collectNotificationSaveErrors(formData, allRules, excludeId).summary;
}

export function runNotificationRuleTest(formData, allRules, limitRules) {
  const snapshot = ruleSnapshotFromForm(formData, limitRules);
  const validation = collectNotificationSaveErrors(formData, allRules, formData.id || undefined, { limitRules });
  const lines = [
    `Event: ${getEventDisplay(snapshot.event)}`,
    `Recipient Type: ${getRecipientLabel(snapshot.recipient) || '—'}`,
    `Recipient: ${getSendToLabel(snapshot.sendTo) || '—'}`,
    `Method: ${getMethodLabel(snapshot.method) || '—'}`,
    `Required / Optional: ${getTypeLabel(snapshot.type) || '—'}`,
    `Timing: ${getTimingLabel(snapshot.timing) || '—'}`,
  ];
  if (snapshot.timing === 'threshold_based') {
    lines.push(
      `Initial Delay: ${formatTimingReferenceDetail(snapshot.initialDelay, limitRules).label}`,
      `Maximum Sends: ${formatTimingReferenceDetail(snapshot.maximumSends, limitRules).label}`,
      `Repeat Interval: ${snapshot.repeatInterval ? formatTimingReferenceDetail(snapshot.repeatInterval, limitRules).label : 'Not Applicable'}`,
    );
  }
  if ((snapshot.conditions || []).length) {
    lines.push('', 'Conditions:', ...snapshot.conditions.map((condition) => `• ${formatConditionLine(condition)}`));
  } else {
    lines.push('', 'No Conditions');
  }
  if (validation.summary.length) {
    return {
      passed: false,
      summary: 'Rule test blocked. Configuration conflicts or missing requirements were found.',
      detail: [...lines, '', 'Issues:', ...validation.summary.map((message) => `• ${message}`)].join('\n'),
    };
  }
  return {
    passed: true,
    summary: 'Rule test passed. Configuration is valid — no notification was sent.',
    detail: [...lines, '', 'No duplicate, conflicting or overlapping Rules detected.'].join('\n'),
  };
}

export function mergeRulesAfterSaveDraft(allRules, payload, publishedBaseline, limitRules) {
  return mergeRulesAfterActivate(allRules, payload, publishedBaseline, limitRules);
}

export function mergeRulesAfterInactivate(allRules) {
  return allRules;
}

export function mergeRulesAfterListActivate(allRules) {
  return allRules;
}

export { normalizeNotificationForm };
